import { FC } from "react";
import { BillingHistoryCard } from "@components/settings/Billing/FlatRate/BillingHistoryCard";
import { useBillingHistory } from "@hooks/useBillingHistory";
import { Loader } from "@design-system/Loader/Loader";
import { useBillingInfo } from "@hooks/useBillingInfo";
import { BillingInfoCard } from "@components/settings/Billing/FlatRate/BillingInfoCard";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import { OrganizationAction } from "@common/userOrganizationRoles";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { PLAN_CATALOG_VERSION } from "@common/flatRateBilling";
import { useCanViewOrganizationBilling } from "@hooks/permissions/organization/useCanViewOrganizationBilling";
import { BillingPageV2 } from "../BillingPageV2/BillingPageV2";
import { ClientsSection } from "./ClientsSection/ClientsSection";
import { MonthlyInvoiceSection } from "./MonthlyInvoiceSection/MonthlyInvoiceSection";
import styles from "./BillingPage.module.scss";

export const BillingPage: FC = () => {
    const activeOrganization = useActiveOrganization();
    const { data: billingInfo, isFetched: isBillingInfoFetched } =
        useBillingInfo();
    const { data: billingHistory, isFetched: isBillingHistoryFetched } =
        useBillingHistory();
    const canManageBilling = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_ORGANIZATION_BILLING,
    );
    const canViewBilling = useCanViewOrganizationBilling();

    const orgCatalogVersion = activeOrganization?.billing?.planCatalogVersion;

    // For V3 orgs, regular admins (VIEW_ORGANIZATION_BILLING) can open the
    // billing page in read-only mode. Owners (MANAGE_ORGANIZATION_BILLING)
    // can additionally manage payment details.
    if (orgCatalogVersion === PLAN_CATALOG_VERSION.V3) {
        if (!canViewBilling) {
            return null;
        }
        return <BillingPageV2 canManageBilling={canManageBilling} />;
    }

    // V2 (metered) orgs and V1 orgs still require full billing management
    // permission.
    if (!canManageBilling) {
        return null;
    }

    // V2 (metered) orgs render the same upcoming-invoice + clients page. The
    // backend `getUpcomingInvoice` endpoint already branches internally to
    // Stripe's `invoices.upcoming`, so the UI just needs to opt in.
    if (orgCatalogVersion === PLAN_CATALOG_VERSION.V2) {
        return <BillingPageV2 canManageBilling={canManageBilling} />;
    }

    if (!isBillingInfoFetched || !isBillingHistoryFetched) {
        return <Loader />;
    }

    const showBillingHistory =
        !!billingHistory?.invoices?.length && canManageBilling;

    return (
        <div className={styles.layout}>
            <ClientsSection />
            <MonthlyInvoiceSection />

            {billingInfo && (
                <div className={styles.full}>
                    <article className={styles.section}>
                        <header className={styles.header}>
                            <h2 className={styles.heading}>Payment details</h2>
                        </header>

                        <main>
                            <BillingInfoCard
                                billingInfo={billingInfo}
                                editable={canManageBilling}
                            />
                        </main>
                    </article>
                </div>
            )}

            {showBillingHistory ? (
                <div className={styles.full}>
                    <article className={styles.section}>
                        <header className={styles.header}>
                            <h2 className={styles.heading}>Billing history</h2>
                        </header>

                        <main>
                            <BillingHistoryCard
                                billingHistory={billingHistory}
                            />
                        </main>
                    </article>
                </div>
            ) : null}
        </div>
    );
};
